import React, { useState, useCallback } from 'react';
import { Form, Input, Button, Alert } from 'antd';
import { UserOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../../../helpers/Axios';

import SecLogo from '../../../../utility/unnamed.png';
import SecBuilding from '../../../../assets/files/img/aaa.jpg';

const authHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
  },
};

const ResendValidation = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [inputDisabled, setInputDisabled] = useState(false);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    if (type === 'success') {
      setTimeout(() => setAlert(null), 5000);
    }
  }, []);

  const onFinish = async (values) => {
    setSubmitting(true);
    setAlert(null);

    try {
      const res = await axios.get(
        'https://api.fortisph.space/gateway/authentication/users/resend_activation/',
        { 
          ...authHeaders,
          params: { username: values.username } 
        }
      );

      const msg =
        `${typeof res.data === 'string' ? res.data : res.data?.message || ''} Please check your email.`;
      showAlert('success', msg);
      setInputDisabled(true);
      setSubmitting(false);
    } catch (error) {
      const data = error?.response?.data;
      const msg = data?.message || 'Failed to resend validation email.';
      showAlert('error', msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* ── Background Image ── */}
      <img
        src={SecBuilding}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />

      {/* ── Dark Gradient Overlay ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(100deg, rgba(0,36,22,0.93) 0%, rgba(0,50,32,0.82) 45%, rgba(0,30,18,0.65) 100%)',
        }}
      />

      {/* ── Main Canvas Layer ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Centered Content Wrapper */}
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 md:p-12">
          
          {/* Centered Form Card */}
          <div
            className="w-full max-w-[460px] bg-white rounded-[20px] p-6 sm:p-9 my-auto"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}
          >
            {/* Header / Branding Inside Card */}
            <header className="flex flex-col items-center text-center mb-6 pb-6 border-b border-gray-100">
              <img
                src={SecLogo}
                alt="SEC Logo"
                style={{ height: 60, width: 'auto', marginBottom: 12 }}
              />
              <h1 className="text-xl font-extrabold text-[#111827] mb-1">
                Resend Validation Email
              </h1>
              <p className="text-sm text-gray-500 mb-3">
                Enter your username to receive a new activation link.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    padding: '2px 10px',
                    borderRadius: 999,
                    background: 'rgba(15,130,105,0.1)',
                    color: '#0f8269',
                    border: '1px solid rgba(15,130,105,0.2)',
                  }}
                >
                  ACCOUNT ACTIVATION
                </span>
              </div>
            </header>

            {alert && (
              <Alert
                type={alert.type}
                message={alert.message}
                showIcon
                closable={alert.type !== 'success'}
                onClose={() => setAlert(null)}
                style={{ marginBottom: 20, borderRadius: 10 }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              name="resend-validation"
              onFinish={onFinish}
              requiredMark={false}
            >
              <Form.Item
                name="username"
                label={
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                    Username <span style={{ color: '#ef4444' }}>*</span>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter your username' }]}
              >
                <Input
                  disabled={inputDisabled}
                  prefix={<UserOutlined style={{ color: '#d1d5db' }} />}
                  placeholder="Enter your username"
                  size="large"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>

              {/* Card Footer Actions */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  width: '100%',
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: '1px solid #f3f4f6',
                }}
              >
                <Link
                  to="/user/login"
                  style={{
                    color: '#0f8269',
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <ArrowLeftOutlined /> Back to Sign In
                </Link>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={submitting || inputDisabled}
                  style={{
                    background: '#0f8269',
                    borderColor: '#0f8269',
                    borderRadius: 10,
                    height: 44,
                    padding: '0 28px',
                    fontWeight: 600,
                    fontSize: 15,
                    marginLeft: 'auto',
                  }}
                >
                  Submit
                </Button>
              </div>
            </Form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ResendValidation;